// Shared helpers for the Coupons & Deals (הטבות) supplier UI — COMY1-359.
// Pure functions over the Admin_system `SupplierHatava` / `HatavaInput`
// shapes (see plans/COMY1-359-hatavot-plan.md), so the list and the modal
// label things the same way.

export const CURRENCY = "₪";

// A catalog reference ({ type, id }) as a stable string key.
export const keyOf = (ref) => `${ref.type}:${ref.id}`;
export const sameRef = (a, b) => a.type === b.type && Number(a.id) === Number(b.id);

// 'YYYY-MM-DD' -> 'DD/MM/YYYY' (the dates travel as plain day strings).
export const fmtDate = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "");

// Today / a date as a LOCAL 'YYYY-MM-DD' — toISOString would shift the day
// around midnight for Asia/Jerusalem users.
export const toLocalISO = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

// Human label of the deal's value ("20%", "₪50", "1+1", "3 ב-2"). Prefers the
// server-built `valueLabel` when the row carries one.
export const valueLabel = (h, t) => {
  if (!h) return "";
  if (h.valueLabel) return h.valueLabel;
  const amount = num(h.valueAmount);
  switch (h.valueType) {
    case "percent":
      return t("value.percent", { amount });
    case "fixed":
    case "price":
    case "bundle":
      return t(`value.${h.valueType}`, { amount, currency: CURRENCY });
    case "bogo":
      return t("value.bogo");
    case "quantity":
      return t("value.quantity", { buy: h.buyQty, pay: h.payQty });
    default:
      return "";
  }
};

// Default title for a deal built from its value — editable by the supplier.
export const autoTitle = (h, t) => {
  const amount = num(h.valueAmount);
  switch (h.valueType) {
    case "percent":
      return amount ? t("auto_title.percent", { amount }) : t("auto_title.discount");
    case "fixed":
    case "price":
    case "bundle":
      return amount
        ? t(`auto_title.${h.valueType}`, { amount, currency: CURRENCY })
        : t(h.valueType === "bundle" ? "auto_title.bundle_generic" : "auto_title.discount");
    case "bogo":
      return t("auto_title.bogo");
    case "quantity":
      return h.buyQty && h.payQty
        ? t("auto_title.quantity", { buy: h.buyQty, pay: h.payQty })
        : t("auto_title.bundle_generic");
    default:
      return "";
  }
};

// One-line "what the deal applies to" for the list row, from the server's
// `targetSummary` (discounts) or `bundleItemsDetailed` (bundles, with quantities).
export const targetText = (h, t, max = 2) => {
  if (!h) return "";
  if (h.kind !== "discount") {
    // Set deals show per-item quantities when > 1 ("A ×2 + B").
    const names = (h.bundleItemsDetailed || []).map((it) => (Number(it.quantity) > 1 ? `${it.name} ×${Number(it.quantity)}` : it.name));
    return names.join(" + ");
  }
  const mode = h.targets?.mode;
  if (mode === "everything") return t("target.everything");
  const s = h.targetSummary || {};
  const parts = [
    ...(s.types || []).map((type) => t("target.all_of_type", { type: t(`item_type.${type}`) })),
    ...(s.categories || []).map((c) => c.name),
    ...(s.items || []).map((it) => it.name),
  ];
  if (!parts.length) return t("target.none");
  const shown = parts.slice(0, max).join(" · ");
  return parts.length > max ? `${shown} ${t("target.more", { count: parts.length - max })}` : shown;
};

// The modal's visual type ('discount' | 'bundle') for a server row.
export const uiType = (h) => (h?.kind === "discount" ? "discount" : "bundle");
// The modal's bundle style for a server kind.
export const bundleStyleOf = (kind) => (kind === "bundle" ? "combined" : kind || "combined");
